/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { Component } from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { FieldType, IEditableFieldProps, IText } from './editable-field';
import { getEditableFieldClassName } from './utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let domPurify: any;
/**
 * Component class for text field
 */
export class TextField extends Component<IEditableFieldProps<IText>, { displayHtml: string }> {
    private isEmpty: boolean;
    constructor(props: { isFocused: boolean } & IEditableFieldProps<IText>) {
        super(props);
        this.state = {
            displayHtml: props.fieldProps.text
        };
        if (this.props.requestContext?.params?.isEditor) {
            // Separating dompurify into a new separate chunk to only download and load when needed in editor mode
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            import(/* webpackChunkName: "msdyn365-dompurify-chunk" */ 'dompurify').then(dp => {
                if (dp && dp.default) {
                    domPurify = dp.default;
                }
            });
        }

        this._onChange = this._onChange.bind(this);
        this._onBlur = this._onBlur.bind(this);
        this._onFocus = this._onFocus.bind(this);
        this.isEmpty = false;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public UNSAFE_componentWillReceiveProps(nextProps: IEditableFieldProps<IText>): void {
        if (this.props.fieldProps.text !== nextProps.fieldProps.text) {
            this.isEmpty = this._isTextEmpty(nextProps.fieldProps.text);

            // hook to handle props changes - when a field is changed between edit enabled/disabled states
            // eslint-disable-next-line react/no-direct-mutation-state
            this.state = {
                displayHtml: this._getDisplayText(nextProps.fieldProps.text)
            };
        }
    }

    public render(): JSX.Element | null {
        if (!!this.props.disabled) {
            return this.props.fieldProps.text ? (
                <ContentEditable
                    html={this.state.displayHtml}
                    tagName={this.props.fieldProps.tag}
                    disabled={true}
                    onChange={this._onChange}
                    className={getEditableFieldClassName(true, this.props.fieldProps.className, this.props.requestContext)}
                />
            ) : null;
        }

        return (
            <ContentEditable
                html={this.state.displayHtml}
                tagName={this.props.fieldProps.tag}
                disabled={false}
                onChange={this._onChange || this._onChange}
                onFocus={this.props.onFocus || this._onFocus}
                onBlur={this.props.onBlur || this._onBlur}
                onMouseOver={this.props.onMouseOver}
                onMouseOut={this.props.onMouseOut}
                className={getEditableFieldClassName(false, this.props.fieldProps.className, this.props.requestContext)}
                {...(this.props.fieldProps.additionalProperties || {})}
            />
        );
    }

    private _onChange(event: ContentEditableEvent): void {
        if (this.props.onChange) {
            const displayValue =
                this.props.type === FieldType.Text || this.props.type === FieldType.Link
                    ? event.currentTarget.innerText
                    : event.target.value;
            this.isEmpty = this._isTextEmpty(displayValue);
            this.setState({
                displayHtml: !this.isEmpty ? displayValue : ''
            });
            event.target.value = displayValue;
            this.props.onChange(event);
        }
    }
    private _onFocus(event: React.FocusEvent<HTMLDivElement>): void {
        const displayText = this._getDisplayText(this.props.fieldProps.text);
        if (this.isEmpty && !!this.props.fieldProps.required && displayText === 'Click to Edit') {
            this.setState({
                displayHtml: ' '
            });
        }
    }

    private _onBlur(event: React.FocusEvent<HTMLDivElement>): void {
        if (this.isEmpty && !!this.props.fieldProps.required) {
            this.setState({
                displayHtml: this._getDisplayText(this.props.fieldProps.text)
            });
        }
    }

    private _getDisplayText(text: string): string {
        return !!this.props.fieldProps.required && this.isEmpty ? this.props.placeholderText || 'Click to Edit' : text;
    }

    private _isTextEmpty(text: string): boolean {
        if (domPurify) {
            const sanitizedText = domPurify
                .sanitize(text, {
                    ALLOWED_TAGS: []
                })
                .replace(/\n/, '');

            return sanitizedText === '';
        }
        return false;
    }
}
